import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import { servicedCounties, vaMedicalCenters, homeBase } from "@/data/site";

const STATES_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

// FIPS ids for serviced states
const SERVICED_STATES = {
  "42": "home", // Pennsylvania
  "24": "soft", // Maryland
  "54": "soft", // West Virginia
  "51": "soft", // Virginia
};

const stateFill = (id) => {
  const lvl = SERVICED_STATES[id];
  if (lvl === "home") return "hsla(46, 98%, 50%, 0.18)";
  if (lvl === "soft") return "hsla(145, 53%, 37%, 0.14)";
  return "hsl(220, 16%, 93%)";
};

const stateStroke = (id) =>
  SERVICED_STATES[id] ? "hsl(220, 18%, 40%)" : "hsl(220, 13%, 82%)";

const radiusVAs = vaMedicalCenters.filter((v) => v.inRadius);

const PROJECTION_CONFIG = { center: [-78.92, 40.33], scale: 5800 };

export const ServiceAreaMap = () => {
  return (
    <div className="relative w-full overflow-hidden rounded-3xl border border-border bg-card p-2 shadow-soft">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={PROJECTION_CONFIG}
        width={800}
        height={500}
        style={{ width: "100%", height: "auto" }}
      >
        <Geographies geography={STATES_URL}>
          {({ geographies }) =>
            geographies.map((geo) => (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                fill={stateFill(geo.id)}
                stroke={stateStroke(geo.id)}
                strokeWidth={0.7}
                style={{
                  default: { outline: "none" },
                  hover: { outline: "none", fill: SERVICED_STATES[geo.id] ? "hsla(46, 98%, 50%, 0.26)" : "hsl(220, 16%, 90%)" },
                  pressed: { outline: "none" },
                }}
              />
            ))
          }
        </Geographies>

        {/* Approximate 2.5-hour service radius around Johnstown */}
        <Marker coordinates={homeBase.coords}>
          <ellipse
            rx={176}
            ry={230}
            fill="hsla(46, 98%, 50%, 0.10)"
            stroke="hsl(46, 98%, 45%)"
            strokeWidth={2}
            strokeDasharray="7 6"
          />
        </Marker>

        {/* Serviced PA counties */}
        {servicedCounties.map((c) => (
          <Marker key={c.name} coordinates={c.coords}>
            <circle r={5} fill="hsl(46, 98%, 50%)" stroke="hsl(220, 22%, 10%)" strokeWidth={1.2} />
            <text
              y={-10}
              textAnchor="middle"
              style={{ fontFamily: "inherit", fontSize: 12, fontWeight: 700, fill: "hsl(220, 22%, 12%)", paintOrder: "stroke", stroke: "hsl(0,0%,100%)", strokeWidth: 3.5 }}
            >
              {c.name}
            </text>
          </Marker>
        ))}

        {/* VA Medical Centers within the radius */}
        {radiusVAs.map((va) => (
          <Marker key={va.name} coordinates={va.coords}>
            <circle r={5} fill="hsl(145, 53%, 37%)" stroke="hsl(0, 0%, 100%)" strokeWidth={1.4}>
              <title>{`${va.name} — ${va.state}`}</title>
            </circle>
            <text
              y={16}
              textAnchor="middle"
              style={{ fontFamily: "inherit", fontSize: 9.5, fontWeight: 600, fill: "hsl(145, 45%, 24%)", paintOrder: "stroke", stroke: "hsl(0,0%,100%)", strokeWidth: 3 }}
            >
              {va.name.replace(/ VAMC.*| \(.*/, "").replace(/ VAMC/, "")}
            </text>
          </Marker>
        ))}

        {/* Home base: Johnstown */}
        <Marker coordinates={homeBase.coords}>
          <g transform="translate(-9, -18)">
            <path
              d="M9 0C4 0 0 4 0 9c0 6.5 9 15 9 15s9-8.5 9-15c0-5-4-9-9-9z"
              fill="hsl(220, 22%, 10%)"
              stroke="hsl(46, 98%, 50%)"
              strokeWidth={1.6}
            />
            <circle cx={9} cy={9} r={3.4} fill="hsl(46, 98%, 50%)" />
          </g>
          <text
            y={-22}
            textAnchor="middle"
            style={{ fontFamily: "inherit", fontSize: 12.5, fontWeight: 800, fill: "hsl(220, 22%, 10%)", paintOrder: "stroke", stroke: "hsl(0,0%,100%)", strokeWidth: 4 }}
          >
            Johnstown
          </text>
        </Marker>
      </ComposableMap>

      {/* Legend */}
      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 border-t border-border px-4 py-3 text-sm">
        <span className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full border border-foreground/40 bg-primary" /> Serviced counties
        </span>
        <span className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full border border-white bg-accent" /> VA Medical Centers
        </span>
        <span className="flex items-center gap-2">
          <span className="inline-block h-0 w-5 border-t-2 border-dashed border-primary" /> ~2.5-hr service radius
        </span>
      </div>
    </div>
  );
};
